// src/context/ThumbnailContext.tsx
import React, { createContext, useContext, useState, ReactNode } from 'react';
import { useThumbnailGeneration } from '../components/ThumbnailGenerator/hooks/useThumbnailGeneration';
import { ThumbnailModal } from '../components/Modal';
import { useNavigate } from 'react-router-dom';
import { useNotification } from './NotificationContext';

interface ThumbnailContextType {
  openThumbnailModal: (options: ThumbnailModalOptions) => void;
  closeThumbnailModal: () => void;
  isModalOpen: boolean;
}

interface ThumbnailModalOptions {
  projectId?: string;
  mediaUrl?: string;
  mediaType?: 'video' | 'audio' | 'none';
  hasExistingCaptions?: boolean;
}

const ThumbnailContext = createContext<ThumbnailContextType | undefined>(undefined);

export const useThumbnailModal = () => {
  const context = useContext(ThumbnailContext);
  if (!context) {
    throw new Error('useThumbnailModal must be used within a ThumbnailProvider');
  }
  return context;
};

interface ThumbnailProviderProps {
  children: ReactNode;
}

export const ThumbnailProvider: React.FC<ThumbnailProviderProps> = ({ children }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalOptions, setModalOptions] = useState<ThumbnailModalOptions>({});
  
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const {
    generateFromDescription,
    generateFromCaptions,
    isLoading,
    status,
    currentStep,
    error,
    reset
  } = useThumbnailGeneration();

  const openThumbnailModal = (options: ThumbnailModalOptions) => {
    setModalOptions(options);
    setIsModalOpen(true);
    reset(); // Reset any previous generation state
  };

  const closeThumbnailModal = () => {
    setIsModalOpen(false);
    setModalOptions({});
    reset();
  };

  const handleProcessContent = async (description?: string): Promise<string> => {
    try {
      let result = null;

      if (description) {
        // Generate from description
        result = await generateFromDescription(description);
      } else if (modalOptions.hasExistingCaptions && modalOptions.projectId) {
        // Generate from existing captions
        result = await generateFromCaptions(modalOptions.projectId);
      } else if (modalOptions.mediaUrl && modalOptions.mediaType === 'video') {
        // This would need file upload implementation
        showNotification({
          title: 'Info',
          message: 'Video processing from modal not yet implemented',
          type: 'info'
        });
        return '';
      }

      if (result) {
        return result.projectId;
      } else {
        throw new Error('Failed to generate thumbnail');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      showNotification({
        title: 'Error',
        message: errorMessage,
        type: 'error'
      });
      throw err;
    }
  };

  const handleCompletionCallback = (projectId: string) => {
    closeThumbnailModal();
    showNotification({
      title: 'Success',
      message: 'Thumbnail generated successfully!',
      type: 'success'
    });
    navigate(`/thumbnail-editor/${projectId}`);
  };
  const value = {
    openThumbnailModal,
    closeThumbnailModal,
    isModalOpen
  };

  return (
    <ThumbnailContext.Provider value={value}>
      {children}
      <ThumbnailModal
        isOpen={isModalOpen}
        onClose={closeThumbnailModal}
        onProcessContent={handleProcessContent}
        mediaType={modalOptions.mediaType || 'none'}
        mediaUrl={modalOptions.mediaUrl}
        isProcessing={isLoading}
        showDashboard={isLoading}
        jobId={null} // We'll implement job tracking later if needed
        onCompletionCallback={handleCompletionCallback}
        projectId={modalOptions.projectId}
      />
    </ThumbnailContext.Provider>
  );
};